// ===== Cliente único do Supabase + autenticação por magic link =====
// Consumido por login.jsx (fluxo de entrada) e pelos testes de RLS da
// Tarefa 13, que abrem o mesmo tipo de cliente para provar isolamento
// pela API REST direta.
import { createClient } from '@supabase/supabase-js';
import { lerLinkDeAcesso } from '../link-de-acesso.js';
import { limparCodigo } from '../codigo-de-acesso.js';

// ===== Erro do link de acesso, capturado ANTES do cliente subir =====
// Quando o magic link expira ou já foi usado, o Supabase devolve o usuário
// para o app com o motivo no endereço:
//   .../#error=access_denied&error_code=otp_expired&error_description=...
// O cliente do Supabase limpa esse trecho ao inicializar. Se a leitura ficasse
// para depois, a informação já teria sumido e o usuário veria a tela de login
// sem explicação nenhuma — que era o comportamento até 19/08, e é o tipo de
// coisa que vira chamado ("cliquei no link e não entrou").
//
// Por isso a captura acontece aqui em cima, no módulo que cria o cliente:
// nada roda antes disto.
function lerErroDoEndereco() {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(
    (window.location.hash || '').replace(/^#/, '') || window.location.search.replace(/^\?/, ''),
  );
  const codigo = params.get('error_code') || params.get('error');
  if (!codigo) return null;
  return {
    codigo,
    descricao: params.get('error_description') || '',
  };
}

export const erroDoLinkDeAcesso = lerErroDoEndereco();

// Link de acesso que veio para o NOSSO dominio (`?token_hash=...&type=email`), capturado no mesmo ponto e pelo mesmo motivo: antes de
// qualquer coisa mexer no endereco. Ver src/link-de-acesso.js. O token so e consumido no clique da tela de confirmacao.
export const linkDeAcesso = typeof window !== 'undefined' ? lerLinkDeAcesso(window.location.search) : null;

// Texto para o usuário. Não repassamos a mensagem crua do servidor: ela vem
// em inglês e fala de "OTP", vocabulário que não diz nada para um vendedor.
export function mensagemDoErroDeAcesso(erro) {
  if (!erro) return null;
  switch (erro.codigo) {
    case 'otp_expired':
      return 'Este link de acesso expirou ou já foi usado. Cada link vale por 1 hora e só funciona uma vez — peça um novo abaixo.';
    case 'access_denied':
      return 'Não foi possível entrar com esse link. Peça um novo abaixo.';
    default:
      return 'Houve um problema com o seu link de acesso. Peça um novo abaixo.';
  }
}

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error('VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY são obrigatórias. Copie .env.example para .env.local.');
}

export const supabase = createClient(url, anonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
});

// Erro vindo da API de autenticacao, em portugues.
//
// Irmao de `mensagemDoErroDeAcesso`, que traduz o erro que volta NO ENDERECO.
// Este traduz o que volta na CHAMADA -- e o caminho que faltava: em 15/09 o Kaio
// levou na tela "For security purposes, you can only request this after 14
// seconds", cru, em ingles, porque o `catch` da tela repassava `err.message`.
//
// Devolve tambem `segundos` quando o servidor diz quanto falta: a tela usa esse
// numero no contador em vez do nosso palpite de 60, e ai o que ela mostra e o
// que o servidor realmente vai aceitar.
export function mensagemDoErroDeEnvio(err) {
  // Erro que NOS levantamos ja vem em portugues e passa direto. A marca e
  // explicita porque adivinhar pelo idioma (procurar acento, por exemplo) erra
  // justamente nas frases curtas sem acento nenhum.
  if (err?.nosso) return { texto: err.message, segundos: 0 };

  const cru = String(err?.message || '');

  const espera = cru.match(/after (\d+) seconds?/i);
  if (espera) {
    const n = Number(espera[1]);
    return { texto: `Aguarde ${n} segundo${n === 1 ? '' : 's'} para pedir outro código.`, segundos: n };
  }
  if (/rate limit/i.test(cru)) {
    return { texto: 'Muitos pedidos de acesso seguidos. Tente de novo em alguns minutos.', segundos: 60 };
  }
  if (/signups? not allowed|not authorized/i.test(cru)) {
    return { texto: 'Este e-mail não tem acesso ao CRM. Fale com o administrador.', segundos: 0 };
  }
  return { texto: 'Não foi possível enviar o código. Tente novamente em instantes.', segundos: 0 };
}

export const DOMINIO_PERMITIDO = 'pdled.com.br';

// Checagem de conveniência — evita uma chamada de rede óbvia. Quem vale de
// verdade é a trigger public.bloqueia_dominio_externo em auth.users
// (supabase/migrations/20260810120024_bloqueia_dominio_externo.sql):
// esta checagem aqui é forjável pelo console do navegador.
// Desde 27/10 o e-mail traz um CODIGO (spec 2026-10-27-login-por-codigo-no-celular); o nome da funcao ficou por ser o do fluxo `signInWithOtp`.
export async function entrarComMagicLink(email) {
  const normalizado = String(email || '').trim().toLowerCase();
  if (!normalizado.endsWith(`@${DOMINIO_PERMITIDO}`)) {
    // `nosso: true` -- ja esta em portugues, nao passa pelo tradutor.
    throw Object.assign(new Error(`Acesso restrito a endereços @${DOMINIO_PERMITIDO}.`), { nosso: true });
  }
  const { error } = await supabase.auth.signInWithOtp({
    email: normalizado,
    options: { emailRedirectTo: `${window.location.origin}/` },
  });
  if (error) throw error;
  return { enviadoPara: normalizado };
}

// Conclui o login do link que veio pelo nosso dominio. O token e de uso unico: depois de gasto, ou se passou de 1 hora, o servidor responde
// `otp_expired`. A sessao nasce aqui e chega ao App por onAuthStateChange, o mesmo caminho do link antigo.
export async function confirmarLinkDeAcesso(link) {
  const { data, error } = await supabase.auth.verifyOtp({ token_hash: link.tokenHash, type: link.tipo });
  if (error) throw error;
  return data.session;
}

// Texto para a tela quando a confirmacao falha: o mesmo vocabulario do link antigo, sem repassar o ingles do servidor.
export function mensagemDoErroDoLink(err) {
  // 429: o Auth limita as verificacoes de token por IP (30 a cada 5 min, no padrao). Dizer "o link expirou" aqui mandaria a pessoa pedir outro
  // link, o que invalida o que ainda valia; o certo e esperar.
  if (err?.status === 429 || err?.code === 'over_request_rate_limit') {
    return 'Muitas tentativas de entrada seguidas. Aguarde alguns minutos e use o mesmo link de novo.';
  }
  const expirado = err?.code === 'otp_expired' || /expired|invalid/i.test(String(err?.message || ''));
  return mensagemDoErroDeAcesso({ codigo: expirado ? 'otp_expired' : 'access_denied' });
}

// Conclui o login do CODIGO digitado na tela (spec 2026-10-27-login-por-codigo-no-celular). E a mesma credencial do link, entregue de outro jeito:
// uso unico, valida pelo tempo de "Email OTP expiration". Documentacao: "Call the "verify OTP" method from your client library with the user's
// email address, the code, and a type of `email`" (https://supabase.com/docs/guides/auth/auth-email-passwordless). A sessao chega ao App por
// onAuthStateChange, o mesmo caminho do link.
export async function confirmarCodigoDeAcesso(email, codigo) {
  const { data, error } = await supabase.auth.verifyOtp({
    email: String(email || '').trim().toLowerCase(),
    token: limparCodigo(codigo),
    type: 'email',
  });
  if (error) throw error;
  if (!data.session) throw new Error('Confirmacao do codigo voltou sem sessao');
  return data.session;
}

// Texto para a tela quando o codigo falha. Irma de `mensagemDoErroDoLink`: o servidor fala ingles e o vendedor nao precisa ler isso.
export function mensagemDoErroDoCodigo(err) {
  // 429: o Auth limita as verificacoes por janela de 5 min. Mandar "pedir outro codigo" aqui MATARIA o codigo que ainda vale (pedir outro invalida
  // o anterior); o certo e esperar e usar o mesmo.
  if (err?.status === 429 || err?.code === 'over_request_rate_limit') {
    return 'Muitas tentativas seguidas. Aguarde alguns minutos e digite o mesmo código de novo.';
  }
  // Codigo errado, vencido e ja usado voltam iguais do servidor (otp_expired / "Token has expired or is invalid"): a tela nao distingue.
  if (err?.code === 'otp_expired' || /expired|invalid/i.test(String(err?.message || ''))) {
    return 'Código incorreto ou vencido. Confira o e-mail mais recente.';
  }
  return 'Não foi possível entrar agora. Tente novamente em instantes.';
}

export async function sessaoAtual() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

// Perfil do usuário logado. É daqui que sai o PAPEL — antes o front fixava
// "closer" no código, então nem o administrador conseguia administrar.
// A leitura passa pela API REST com a chave anônima, sob RLS: a política
// `profiles_sel` deixa o usuário ler a própria linha. Não usamos service_role
// em nada que rode no navegador.
//
// Devolve null quando não existe perfil — caso real de quem entrou antes do
// gatilho `on_auth_user_created` (0025) existir. Quem chama trata como "não
// liberado", nunca como "papel vazio, segue em frente".
export async function perfilAtual(userId) {
  if (!userId) return null;
  const { data, error } = await supabase
    .from('profiles')
    .select('id, nome, iniciais, email, papel, id_pessoa_vendedor, ativo, telefone')
    .eq('id', userId)
    .maybeSingle();
  if (error) throw error;
  return data ?? null;
}

// scope: 'global' é o que faz o logout valer em todas as abas e
// dispositivos — exigência do spec.
export async function sair() {
  const { error } = await supabase.auth.signOut({ scope: 'global' });
  if (error) throw error;
}

// Encerra as OUTRAS sessoes e mantem esta (levantamento de 02/10: o botao de "Meu perfil" so mostrava a confirmacao).
// Supabase, auth.signOut: "Sign out of all other sessions, keep the current one" -- scope 'others'
// (https://supabase.com/docs/reference/javascript/auth-signout). RESSALVA da mesma pagina: "There is no way to revoke a
// user's access token jwt until it expires." Ou seja, os outros aparelhos perdem a renovacao agora, mas o token que ja
// tem continua valendo ate vencer (por padrao, ate uma hora). A tela diz isso.
export async function sairDasOutrasSessoes() {
  const { error } = await supabase.auth.signOut({ scope: 'others' });
  if (error) throw error;
}

// Logout por INATIVIDADE usa escopo 'local': encerra só este navegador ocioso,
// não os outros dispositivos do usuário. Quem ficou parado foi esta máquina —
// derrubar o celular que a pessoa está usando seria punição indevida. A
// inatividade é medida por navegador (localStorage), então local é o escopo
// coerente. Ver src/inatividade.js.
export async function sairLocal() {
  const { error } = await supabase.auth.signOut({ scope: 'local' });
  if (error) throw error;
}
